import Seo from "@/components/feature/Seo";
import Container from "@/components/base/Container";
import PageHero from "@/components/base/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import SolicitudForm from "./components/SolicitudForm";
import { processSteps } from "@/mocks/site";
import { useCompanyInfo } from "@/hooks/usePublicContent";

export default function SolicitarServicio() {
  const companyInfo = useCompanyInfo();

  return (
    <>
      <Seo
        title="Solicitar servicio"
        description="Solicita tu servicio de transporte, mudanza, acarreo o distribución en línea. Completa el formulario y recibe un número de seguimiento para tu solicitud."
        path="/solicitar-servicio"
      />
      <PageHero
        title="Solicita tu servicio"
        subtitle="Completa el formulario y te asignaremos la unidad y el equipo adecuados. Recibirás un número de solicitud para dar seguimiento."
        crumbs={[{ label: "Inicio", to: "/" }, { label: "Solicitar servicio" }]}
        image="https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20a%20logistics%20coordinator%20reviewing%20a%20tablet%20beside%20a%20fleet%20of%20white%20trucks%20in%20a%20clean%20terminal%2C%20professional%20corporate%20transport%20photography%2C%20deep%20blue%20tones%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-solicitar&orientation=landscape"
      />

      <section className="bg-background-50 py-12 md:py-16">
        <Container>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr] lg:gap-10">
            <div>
              <SectionHeading
                eyebrow="Formulario de solicitud"
                title="Cuéntanos qué necesitas mover"
                description="Mientras más detalles nos compartas, más precisa será nuestra propuesta de unidad y servicio."
              />
              <div className="mt-8">
                <SolicitudForm />
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="text-base font-heading font-semibold text-foreground-950">
                  ¿Cómo funciona?
                </h2>
                <ol className="mt-5 space-y-5">
                  {processSteps.map((step, index) => (
                    <li key={step.id} className="flex gap-3.5">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-label font-semibold text-background-50 dark:text-foreground-950">
                        {index + 1}
                      </span>
                      <div>
                        <h3 className="text-sm font-heading font-semibold text-foreground-950">
                          {step.titulo}
                        </h3>
                        <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                          {step.descripcion}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="text-base font-heading font-semibold text-foreground-950">
                  ¿Prefieres hablar con alguien?
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  Nuestro equipo está disponible para ayudarte a elegir la mejor opción.
                </p>
                <ul className="mt-5 space-y-3 text-sm">
                  <li>
                    <a
                      href={companyInfo.telefonoLink}
                      className="flex items-center gap-3 text-foreground-700 transition-colors hover:text-primary-600"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                        <i className="ri-phone-line text-base leading-none" aria-hidden="true"></i>
                      </span>
                      {companyInfo.telefono}
                    </a>
                  </li>
                  <li>
                    <a
                      href={companyInfo.emailLink}
                      className="flex items-center gap-3 break-all text-foreground-700 transition-colors hover:text-primary-600"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                        <i className="ri-mail-line text-base leading-none" aria-hidden="true"></i>
                      </span>
                      {companyInfo.email}
                    </a>
                  </li>
                  <li>
                    <a
                      href={companyInfo.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-foreground-700 transition-colors hover:text-primary-600"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                        <i className="ri-whatsapp-line text-base leading-none" aria-hidden="true"></i>
                      </span>
                      {companyInfo.whatsapp}
                    </a>
                  </li>
                </ul>
                <p className="mt-5 flex items-center gap-2 border-t border-background-200 pt-4 text-xs text-foreground-500">
                  <i className="ri-time-line text-sm leading-none" aria-hidden="true"></i>
                  {companyInfo.horario}
                </p>
              </div>

              <div className="rounded-lg border border-accent-300 bg-accent-50 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-shield-check-line text-lg leading-none" aria-hidden="true"></i>
                </span>
                <h2 className="mt-3 text-sm font-heading font-semibold text-accent-900">
                  Atención personalizada
                </h2>
                <p className="mt-1.5 text-xs leading-relaxed text-accent-900">
                  Cada solicitud es revisada por un coordinador real. Sin bots, sin respuestas
                  automáticas genéricas.
                </p>
              </div>
            </aside>
          </div>
        </Container>
      </section>
    </>
  );
}